import type { Relation, Table } from '@/types'
import { calculateImportedTablesLayout } from '@/services/incrementalImportService'
import { pinAutomaticRelationshipRoutes } from '@/utils/paths'

export const SQL_DIALECTS = [
  'postgresql',
  'mysql',
  'mariadb',
  'sqlserver',
  'oracle',
  'sqlite',
  'generic',
] as const

export type SqlDialect = (typeof SQL_DIALECTS)[number]

export interface SqlImportMessage {
  level: 'warning' | 'error' | 'ignored'
  message: string
  meta: Record<string, unknown>
}

export interface SqlImportSummary {
  dialect: SqlDialect
  detectedDialect: SqlDialect
  schemas: string[]
  tableCount: number
  columnCount: number
  primaryKeyCount: number
  foreignKeyCount: number
  indexCount: number
  commentCount: number
  ignoredCommandCount: number
  warningCount: number
  errorCount: number
  warnings: SqlImportMessage[]
  errors: SqlImportMessage[]
  ignoredCommands: SqlImportMessage[]
}

/** Raw parse result returned by the backend (`/import/sql` and `/parse-sql`), before layout. */
export interface ParsedSqlModel {
  tables: Table[]
  rels: Relation[]
  warnings: string[]
  summary: SqlImportSummary
}

export type SqlImportModel = ParsedSqlModel

/**
 * Applies client-side positioning to the backend-parsed tables. Parsing itself now lives on
 * the backend (`projectApi.importSqlParse` / `projectApi.parseSql`); the editor keeps ownership
 * of layout and of applying the result (replace / incremental).
 */
export function applyImportLayout(parsed: ParsedSqlModel): SqlImportModel {
  const tables = calculateImportedTablesLayout({
    currentTables: [],
    importedTables: parsed.tables,
    relationships: parsed.rels,
  })
  return {
    tables,
    rels: pinAutomaticRelationshipRoutes(parsed.rels, tables),
    warnings: parsed.warnings,
    summary: parsed.summary,
  }
}
